import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAtom } from 'jotai'
import { userAtom } from '../state/auth'
import { useWebSocket } from '../hooks/useWebSocket'
import { useNotification } from '../hooks/useNotification'

/**
 * #199 — Global staff-side toast layer.
 *
 * Mounted once at the App-tree level for non-client users. Opens its own
 * WebSocket connection to listen for events that matter to the team
 * regardless of which page they're currently on. Today this is scoped to
 * mock interview feedback ready; over time this is the natural home for
 * other team-awareness pings (lead created, intake complete, etc.).
 *
 * Each per-page useWebSocket call still gets its own connection for that
 * page's local handlers (refetches etc.). This component does NOT replace
 * those — it just adds the cross-page toast.
 */
export default function GlobalStaffNotifications() {
  const [user] = useAtom(userAtom)
  const navigate = useNavigate()
  const { showInfo } = useNotification()

  const handlers = useMemo(() => {
    return {
      mock_interview_feedback_ready: (message: { data: unknown }) => {
        const data = (message?.data ?? {}) as {
          clientId?: string
          clientName?: string
          interviewId?: string
        }
        const name = data.clientName || 'A client'
        showInfo(`${name} completed a mock interview — feedback ready for your review`)
        // Best-effort click-through: navigate to the client's detail page.
        // Wrapping in a setTimeout so the toast has time to render before any
        // potential auto-dismiss interaction.
        if (data.clientId) {
          // Stash the clientId on window so a future toast-click handler can
          // pick it up. For now the navigate is just a non-interactive toast.
          ;(window as unknown as { __lastMockToastClient?: string }).__lastMockToastClient =
            data.clientId
        }
        void navigate
      },
    }
  }, [showInfo, navigate])

  // Only mount the connection for staff users. Portal clients don't need
  // these toasts and the WS endpoint is the same — no point in extra
  // connections.
  const isStaff = user && user.role !== 'client'
  useWebSocket(isStaff ? handlers : {})

  return null
}
